import React, {createContext, useContext, useReducer, useEffect} from 'react';

const FinancialContext = createContext();

export const ACCOUNTS = [
  {id: 'income_hub', name: 'Income Hub', type: 'Income', category: 'income', purpose: 'Primary router for all incoming funds.', tag: 'Main Checking'},
  {id: 'investing_transfer', name: 'Investing Transfer', type: 'Investment', category: 'investment', purpose: 'Staging for wealth-building assets.', tag: 'IRA'},
  {id: 'bills', name: 'Bills / Fixed Expenses', type: 'Drain', category: 'drain', purpose: 'Fixed operational costs and subscriptions.', tag: 'Main Checking'},
  {id: 'emergency', name: 'Emergency Fund', type: 'Drain', category: 'drain', purpose: 'Safety net (3-6 months expenses).', tag: 'High-yield savings'},
  {id: 'discretionary', name: 'Discretionary', type: 'Drain', category: 'drain', purpose: 'Guilt-free lifestyle spending.', tag: 'Secondary Checking'},
  {id: 'spouse_freedom', name: 'Spouse Freedom Fund', type: 'Drain', category: 'drain', purpose: 'Dedicated independence allocation.', tag: 'Cash App'},
  {id: 'vacation', name: 'Vacation Fund', type: 'Drain', category: 'drain', purpose: 'Travel and leisure savings.', tag: 'High-yield savings'}
];

export const getSortedAccounts = (customAccounts = []) => {
  const categories = ['income', 'investment', 'drain'];
  return categories.flatMap(cat => [
    ...ACCOUNTS.filter(account => account.category === cat),
    ...customAccounts.filter(account => account.category === cat)
  ]);
};

export const normalizeToMonthly = (amount, frequency) => {
  const value = parseFloat(amount) || 0;
  switch (frequency) {
    case 'weekly': return value * 52 / 12;
    case 'biweekly': return value * 26 / 12;
    case 'yearly': return value / 12;
    default: return value;
  }
};

const initialState = {
  goals: {
    targetAmount: 1500000, targetAge: 65, currentAge: 30, expectedROI: 7,
    startingNetWorth: 50000, inflationRate: 3, downturnYear: 0,
    downturnSeverity: 0, downturnDuration: 1
  },
  settings: {displayFrequency: 'default', flowFilters: []},
  customAccounts: [],
  investmentSchedule: {type: 'fixed', stepAmount: 0, stepFrequency: 'monthly'},
  incomeStreams: [{id: 1, name: 'Primary Salary', amount: 6500, frequency: 'monthly', accountType: 'income_hub'}],
  expenses: [{id: 1, name: 'Living Expenses', amount: 3500, frequency: 'monthly', accountType: 'bills'}],
  investmentBuckets: [{id: 1, name: 'Index Funds', amount: 250, frequency: 'monthly', icon: 'TrendingUp', color: 'blue', accountType: 'investing_transfer'}],
  roadmapProgress: [],
  calculations: {
    monthlyIncome: 0, monthlyExpenses: 0, monthlyInvested: 0,
    requiredMonthlyInvestment: 0, netSurplus: 0, projectedWealth: 0,
    realProjectedWealth: 0, yearsToGoal: 0, savingsRate: 0
  }
};

const updateById = (list, id, changes) => list.map(item => item.id === id ? {...item, ...changes} : item);

function financialReducer(state, action) {
  switch (action.type) {
    case 'IMPORT_DATA': return {...state, ...action.payload};
    case 'LOAD_SCENARIO': return {...state, goals: {...state.goals, ...action.payload.goals}, incomeStreams: action.payload.incomeStreams, expenses: action.payload.expenses, investmentBuckets: action.payload.investmentBuckets};
    case 'UPDATE_GOALS': return {...state, goals: {...state.goals, ...action.payload}};
    case 'UPDATE_SETTINGS': return {...state, settings: {...state.settings, ...action.payload}};
    case 'ADD_CUSTOM_ACCOUNT': return {...state, customAccounts: [...state.customAccounts, action.payload]};
    case 'ADD_INCOME': return {...state, incomeStreams: [...state.incomeStreams, action.payload]};
    case 'UPDATE_INCOME': return {...state, incomeStreams: updateById(state.incomeStreams, action.payload.id, action.payload)};
    case 'REMOVE_INCOME': return {...state, incomeStreams: state.incomeStreams.filter(item => item.id !== action.id)};
    case 'ADD_EXPENSE': return {...state, expenses: [...state.expenses, action.payload]};
    case 'UPDATE_EXPENSE': return {...state, expenses: updateById(state.expenses, action.payload.id, action.payload)};
    case 'REMOVE_EXPENSE': return {...state, expenses: state.expenses.filter(item => item.id !== action.id)};
    case 'ADD_INVESTMENT': return {...state, investmentBuckets: [...state.investmentBuckets, action.payload]};
    case 'UPDATE_INVESTMENT': return {...state, investmentBuckets: updateById(state.investmentBuckets, action.payload.id, action.payload)};
    case 'REMOVE_INVESTMENT': return {...state, investmentBuckets: state.investmentBuckets.filter(item => item.id !== action.id)};
    case 'UPDATE_CALCULATIONS': return {...state, calculations: {...state.calculations, ...action.payload}};
    case 'TOGGLE_ROADMAP_STEP': {
      const isComplete = state.roadmapProgress.includes(action.payload);
      return {...state, roadmapProgress: isComplete ? state.roadmapProgress.filter(id => id !== action.payload) : [...state.roadmapProgress, action.payload]};
    }
    case 'UPDATE_INVESTMENT_SCHEDULE': return {...state, investmentSchedule: {...state.investmentSchedule, ...action.payload}};
    default: return state;
  }
}

export function FinancialProvider({children}) {
  const [state, dispatch] = useReducer(financialReducer, initialState);
  useEffect(() => {
    const {goals, incomeStreams, expenses, investmentBuckets, investmentSchedule} = state;
    const monthlyIncome = incomeStreams.reduce((total, item) => total + normalizeToMonthly(item.amount, item.frequency), 0);
    const monthlyExpenses = expenses.reduce((total, item) => total + normalizeToMonthly(item.amount, item.frequency), 0);
    const monthlyInvested = investmentBuckets.reduce((total, item) => total + normalizeToMonthly(item.amount, item.frequency), 0);
    const netSurplus = monthlyIncome - monthlyExpenses - monthlyInvested;
    const years = Math.max(0, goals.targetAge - goals.currentAge);
    const months = years * 12;
    const rate = goals.expectedROI / 100 / 12;
    let requiredMonthlyInvestment = 0;
    if (months > 0) {
      const futureValue = goals.targetAmount;
      const presentValue = parseFloat(goals.startingNetWorth) || 0;
      if (rate > 0) {
        const factor = Math.pow(1 + rate, months);
        requiredMonthlyInvestment = (futureValue - presentValue * factor) / ((factor - 1) / rate);
      } else {
        requiredMonthlyInvestment = (futureValue - presentValue) / months;
      }
    }
    let projectedWealth = parseFloat(goals.startingNetWorth) || 0;
    for (let year = 1; year <= years; year++) {
      const downturn = goals.downturnYear > 0 && year >= goals.downturnYear && year < goals.downturnYear + goals.downturnDuration;
      let currentRate = rate;
      if (downturn) currentRate = Math.pow(1 - goals.downturnSeverity / 100, 1 / 12) - 1;
      let contribution = monthlyInvested;
      if (investmentSchedule.type === 'linear_step') {
        contribution += (year - 1) * normalizeToMonthly(investmentSchedule.stepAmount, investmentSchedule.stepFrequency || 'monthly');
      }
      for (let month = 0; month < 12; month++) {
        projectedWealth = (projectedWealth + contribution) * (1 + currentRate);
      }
    }
    const realProjectedWealth = projectedWealth / Math.pow(1 + goals.inflationRate / 100, years);
    dispatch({
      type: 'UPDATE_CALCULATIONS',
      payload: {
        monthlyIncome, monthlyExpenses, monthlyInvested,
        requiredMonthlyInvestment: Math.max(0, requiredMonthlyInvestment),
        netSurplus, projectedWealth: Math.max(0, projectedWealth),
        realProjectedWealth: Math.max(0, realProjectedWealth),
        yearsToGoal: years,
        savingsRate: monthlyIncome > 0 ? monthlyInvested / monthlyIncome * 100 : 0
      }
    });
  }, [state.goals, state.incomeStreams, state.expenses, state.investmentBuckets, state.investmentSchedule]);
  return <FinancialContext.Provider value={{state, dispatch}}>{children}</FinancialContext.Provider>;
}

export const useFinancial = () => useContext(FinancialContext);